/**
 * API サーバ・LIFF アプリ・管理画面で共有する型と定数。
 * 「提供資料で確定している概念」だけを型にしている。未確定事項は docs/todo.txt。
 */
import { z } from 'zod';
// =============================================================================
// 列挙（Prisma の enum と 1:1 で対応させる）
// =============================================================================
/** 抽選タイプ。提案書 p.5「選べる抽選タイプ」 */
export const LotteryType = {
    ROULETTE: 'ROULETTE',
    SCRATCH: 'SCRATCH',
};
/** くじ参加回数ルール。提案書 p.5「1日1回 / 期間中1回 / 複数回参加可」 */
export const EntryRule = {
    ONCE_PER_DAY: 'ONCE_PER_DAY',
    ONCE_PER_PERIOD: 'ONCE_PER_PERIOD',
    UNLIMITED: 'UNLIMITED',
};
export const EventStatus = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
    PAUSED: 'PAUSED',
    ENDED: 'ENDED',
};
/** 抽選結果の状態。提案書 p.5「当選は仮状態。電子スタンプ押印後にクーポン付与が完了」 */
export const EntryStatus = {
    /** 仮当選。押印待ち */
    PROVISIONAL: 'PROVISIONAL',
    /** 押印で確定 */
    CONFIRMED: 'CONFIRMED',
    /** 押印されないまま期限切れ */
    EXPIRED: 'EXPIRED',
    /** ハズレ（参加賞もない）。押印の対象にならない */
    NO_PRIZE: 'NO_PRIZE',
};
/** 外部システムへの特典付与の状態 */
export const RewardGrantStatus = {
    NOT_APPLICABLE: 'NOT_APPLICABLE',
    PENDING: 'PENDING',
    GRANTED: 'GRANTED',
    FAILED: 'FAILED',
};
// =============================================================================
// ドメイン定数
// =============================================================================
/** 賞の登録上限。提案書 p.9「賞設定（最大10項目）」 */
export const MAX_PRIZES_PER_EVENT = 10;
/** 当選確率の分母（basis point＝万分率）。10000 = 100% */
export const WIN_RATE_DENOMINATOR = 10_000;
/**
 * 仮当選が有効な時間（分）。押印までの猶予で、開催終了時刻の方が早ければそちらで切る。
 * 開発側の仮仕様（顧客未確認。docs/todo.txt T-08）。CocodePit 入口トークンの 10 分とは別の期限。
 */
export const PROVISIONAL_ENTRY_TTL_MINUTES = 30;
/**
 * 「1日1回」判定に使う営業日の切替時刻（日本時間の時）。
 * 深夜の滞在中に日付が変わって再参加できてしまうのを避けるための開発側仮仕様（docs/todo.txt T-19）。
 */
export const BUSINESS_DAY_START_HOUR_JST = 6;
// =============================================================================
// バリデーションスキーマ（API の入出力で共用）
// =============================================================================
export const prizeInputSchema = z.object({
    /** 既存の賞を更新する場合の ID。抽選結果が参照している賞を作り直さないため、編集時は必ず送る */
    id: z.string().uuid().optional(),
    /** 表示順・階層。1 = 特賞、以降 1等/2等/... の順 */
    rank: z.number().int().min(1).max(MAX_PRIZES_PER_EVENT),
    name: z.string().min(1).max(100),
    description: z.string().max(1000).default(''),
    imageUrl: z.string().url().nullable().default(null),
    /** 当選本数。参加賞（isConsolation=true）の場合は null で「無制限」 */
    stock: z.number().int().min(0).nullable(),
    /** 当選確率（万分率）。参加賞は null（ハズレ受け皿） */
    winRateBp: z.number().int().min(0).max(WIN_RATE_DENOMINATOR).nullable(),
    /** 参加賞かどうか。イベントにつき 1 件のみ */
    isConsolation: z.boolean().default(false),
    /** 外部システムで付与する特典のコード。null なら外部付与しない（現物配布など） */
    externalRewardCode: z.string().min(1).max(100).nullable().default(null),
});
/** イベントに連携する CocodePit（＝対象店舗） */
export const eventCocodePitInputSchema = z.object({
    /** 製造管理番号（端末に印字されている値） */
    pcode: z.string().trim().min(1).max(50),
    /** その場所で押すスタンプのコード（複数可） */
    stampCodes: z.array(z.string().trim().min(1).max(100)).max(20).default([]),
    placement: z.string().max(120).default(''),
    purpose: z.string().max(200).default(''),
});
export const eventInputSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).default(''),
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    lotteryType: z.nativeEnum(LotteryType),
    entryRule: z.nativeEnum(EntryRule),
    /** 特典の受取に公式 LINE 友だち登録を必須とするか。提供資料の既定は true */
    requireFriendForReward: z.boolean().default(true),
    topImageUrl: z.string().url().nullable().default(null),
    topTitle: z.string().max(120).default(''),
    topBody: z.string().max(2000).default(''),
    resultImageUrl: z.string().url().nullable().default(null),
    resultBody: z.string().max(2000).default(''),
    lineGuideImageUrl: z.string().url().nullable().default(null),
    lineGuideBody: z.string().max(2000).default(''),
    stampEffectImageUrl: z.string().url().nullable().default(null),
    stampEffectBody: z.string().max(2000).default(''),
});
export const adminLoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(200),
});
// =============================================================================
// ユーティリティ
// =============================================================================
/** 賞の階層表示名。rank=1 を特賞として扱う */
export function defaultPrizeLabel(rank, isConsolation) {
    if (isConsolation)
        return '参加賞';
    if (rank === 1)
        return '特賞';
    return `${rank - 1}等`;
}
//# sourceMappingURL=index.js.map